import React, { useState } from 'react';
import { X, Upload } from 'lucide-react';
import {
  Plus,
  Square,
  LayoutTemplate,
} from 'lucide-react';
import LayersTree from './LayersTree';

/**
 * LayersTab - Contains the tree view and add element button
 */
export default function LayersTab({
  sections,
  selectedSection,
  setSelectedSection,
  allSectionTypes,
  handleDeleteSection,
  handleQuickUpdate,
  handleDragEnd,
  sensors,
  onShowComponentPalette,
  onMoveComponent,
  onOpenTemplateLibrary,
  onExportSection,
  onImportSection,
  onReorderSections,
  // Site sections props
  siteSections,
  onCreateSiteSection,
  page,
  onUpdateSection,
}) {
  const [saveSiteSectionModal, setSaveSiteSectionModal] = useState({
    isOpen: false,
    section: null,
    name: '',
  });

  // Handler for opening the "Save as Site Section" modal
  const handleSaveAsSiteSection = (section) => {
    // Open modal with section, pre-fill name from section content if available
    const defaultName = section.content?.name || 'Untitled Section';
    setSaveSiteSectionModal({
      isOpen: true,
      section,
      name: defaultName,
    });
  };

  // Handler for confirming and saving the site section
  const handleConfirmSaveSiteSection = async () => {
    const { section, name } = saveSiteSectionModal;
    if (!section || !name.trim()) return;

    // Create site section with section content
    const result = await onCreateSiteSection({
      name: name.trim(),
      section_type: section.section_type || 'section',
      content: section.content,
      order_index: 0,
    });

    if (result) {
      // Update the page section to reference the site section
      // Add siteSection_id inside content to mark it as linked
      const updatedSection = {
        ...section,
        content: {
          ...section.content,
          siteSection_id: result.id,
        },
      };
      // Call the section update handler to save this change
      if (onUpdateSection) {
        onUpdateSection(section.id, updatedSection);
      }
    }

    // Close modal
    setSaveSiteSectionModal({ isOpen: false, section: null, name: '' });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Add Element & Browse Templates */}
      <div className="p-3 border-b border-gray-100">
        <button
          onClick={() => onShowComponentPalette()}
          className="w-full flex items-center justify-center space-x-2 p-2 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
        >
          <Plus className="w-4 h-4 text-blue-600" />
          <span className="text-xs font-medium text-blue-900">Add Element</span>
        </button>

        {/* Browse Templates Button */}
        <button
          onClick={onOpenTemplateLibrary}
          className="w-full flex items-center justify-center space-x-2 p-2 mt-2 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
        >
          <LayoutTemplate className="w-4 h-4 text-gray-600" />
          <span className="text-xs font-medium text-gray-700">Browse Templates</span>
        </button>
      </div>

      {/* Layers Tree */}
      <div className="flex-1 overflow-y-auto">
        {sections.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <Square className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-xs text-gray-500">No sections yet</p>
            <p className="text-[11px] text-gray-400 mt-1">Add a section to get started</p>
            <button
              onClick={onImportSection}
              className="flex items-center gap-1.5 mt-3 px-3 py-1.5 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
            >
              <Upload className="w-3.5 h-3.5" />
              Import Section
            </button>
          </div>
        ) : (
          <LayersTree
            sections={sections}
            selectedSection={selectedSection}
            setSelectedSection={setSelectedSection}
            allSectionTypes={allSectionTypes}
            handleDeleteSection={handleDeleteSection}
            handleQuickUpdate={handleQuickUpdate}
            handleDragEnd={handleDragEnd}
            sensors={sensors}
            onShowComponentPalette={onShowComponentPalette}
            onMoveComponent={onMoveComponent}
            onExportSection={onExportSection}
            onImportSection={onImportSection}
            onReorderSections={onReorderSections}
            siteSections={siteSections}
            page={page}
            onSaveAsSiteSection={handleSaveAsSiteSection}
          />
        )}
      </div>

      {/* Save as Site Section Modal */}
      {saveSiteSectionModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Save as Site Section</h3>
              <button
                onClick={() => setSaveSiteSectionModal({ isOpen: false, section: null, name: '' })}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              This section will be shared across all pages in the site. Changes made on any page will update everywhere.
            </p>
            <input
              type="text"
              value={saveSiteSectionModal.name}
              onChange={(e) => setSaveSiteSectionModal(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Section name (e.g., Header, Footer)"
              className="w-full px-3 py-2 border rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setSaveSiteSectionModal({ isOpen: false, section: null, name: '' })}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSaveSiteSection}
                disabled={!saveSiteSectionModal.name.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
